import { useCallback, useState, useEffect } from 'react';
import { useQuery, useMutation, useApolloClient } from '@apollo/react-hooks';

import { useSessionData } from '@confluence/session-data';

import { HomeWidgetsQuery } from './HomeWidgetsQuery.graphql';
import { HomeWidgetsMutation } from './HomeWidgetsMutation.graphql';
import type { HomeWidgetsQuery as HomeWidgetsData } from './__types__/HomeWidgetsQuery';
import { HomeWidgetState } from './__types__/HomeWidgetsQuery';
import type {
	HomeWidgetsMutation as HomeWidgetsMutationData,
	HomeWidgetsMutationVariables as HomeWidgetsMutationVars,
} from './__types__/HomeWidgetsMutation';

export const useHomeWidgetState = (
	id: string,
	defaultState?: HomeWidgetState,
	initialStateOverride?: HomeWidgetState,
) => {
	const { isLicensed } = useSessionData();
	const {
		data,
		loading,
		error: queryError,
	} = useQuery<HomeWidgetsData>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Read https://go/connie-relay-migration-fyi
		HomeWidgetsQuery,
		{
			errorPolicy: 'all',
			// Instead of skipping if !isLicensed, we set the policy to cache-only to prevent
			// the network call but still allow us to use Apollo cache as source of truth.
			fetchPolicy: isLicensed ? 'cache-first' : 'cache-only',
		},
	);
	const [updateWidgetState, { error: mutationError }] = useMutation<
		HomeWidgetsMutationData,
		HomeWidgetsMutationVars
	>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Read https://go/connie-relay-migration-fyi
		HomeWidgetsMutation,
		{
			ignoreResults: true,
		},
	);

	const [stateOverride, setStateOverride] = useState(initialStateOverride);
	useEffect(() => {
		setStateOverride(initialStateOverride);
	}, [initialStateOverride]);

	// Use the widget state override, falling back to saved prefrence, falling back to defaultState, falling back to collapsed
	const widgetState =
		stateOverride ||
		data?.userPreferences.homeWidgets.find((widget) => widget.id === id)?.state ||
		defaultState ||
		HomeWidgetState.COLLAPSED;

	const apollo = useApolloClient();
	const setWidgetState = useCallback(
		(newState: HomeWidgetState) => {
			if (isLicensed) {
				void updateWidgetState({
					variables: {
						id,
						state: newState,
					},
				});
			}

			const homeWidgets = (data?.userPreferences.homeWidgets || []).filter(
				(widget) => widget.id !== id,
			);

			homeWidgets.push({
				id,
				state: newState,
				__typename: 'HomeWidget',
			});

			/**
			 * This writeQuery serves two purposes:
			 *   1. To initially populate the cache in the case where
			 *      a preference hasn't yet been saved or the user is unlicensed.
			 *   2. To optimistically update the cache, since we're doing #1 anyway
			 */
			apollo.writeQuery<HomeWidgetsData>({
				query: HomeWidgetsQuery,
				data: {
					userPreferences: {
						homeWidgets,
						__typename: 'UserPreferences',
					},
				},
			});

			// After the user explicitly changes state, clear the state override
			setStateOverride(undefined);
		},
		[id, data, apollo, isLicensed, updateWidgetState],
	);

	const error = mutationError || queryError;

	return { widgetState, setWidgetState, loading, error };
};
